import {
  type CostType,
  currencyDigits,
  type ExpensePurpose,
  FUEL_UNIT_DECIMALS,
  type FuelUnit,
  fromQty,
} from "@bt/domain";
import {
  CircleEllipsisIcon,
  FileBadgeIcon,
  FlagIcon,
  FuelIcon,
  LandmarkIcon,
  type LucideIcon,
  PackagePlusIcon,
  PlugZapIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  SparklesIcon,
  SquareParkingIcon,
  UmbrellaIcon,
  WalletIcon,
  WrenchIcon,
} from "lucide-react";
import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

/** One icon per cost type, so a row says what it was at a glance (with its text, never alone). */
export const COST_TYPE_ICONS: Readonly<Record<CostType, LucideIcon>> = {
  fuel: FuelIcon,
  charging: PlugZapIcon,
  workshop: WrenchIcon,
  roadworthiness_fee: ShieldCheckIcon,
  insurance: UmbrellaIcon,
  vehicle_tax: LandmarkIcon,
  registration_fees: FileBadgeIcon,
  care: SparklesIcon,
  parking_tolls: SquareParkingIcon,
  accessories: PackagePlusIcon,
  event_fee: FlagIcon,
  other: ReceiptIcon,
};

export const PURPOSE_ICONS: Readonly<Record<ExpensePurpose, LucideIcon>> = {
  running: WalletIcon,
  event: FlagIcon,
  other: CircleEllipsisIcon,
};

/** The cost type's icon on a soft tile; events get the brand's tint. */
export function CostTypeTile({
  costType,
  purpose,
  className,
}: {
  costType: CostType;
  purpose: ExpensePurpose;
  className?: string;
}) {
  const Icon = COST_TYPE_ICONS[costType];
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-lg border",
        purpose === "event"
          ? "border-brand/30 bg-brand/10 text-brand"
          : "border-border bg-muted text-muted-foreground",
        className,
      )}
    >
      <Icon className="size-4" />
    </span>
  );
}

/** Formats what the server worked out: quantities, prices per unit. */
export function useExpenseFormat() {
  const { t } = useTranslation();
  const format = useFormat();
  return {
    /** "42,51 l". */
    fuelQty: (qty: number, unit: FuelUnit) =>
      `${format.number(fromQty(qty), FUEL_UNIT_DECIMALS[unit])} ${t(`vehicleManagement.fuelUnits.${unit}`)}`,
    /** "1,789 €/l" from thousandths of a minor unit. */
    pricePerUnit: (thousandths: number, currency: string, unit: FuelUnit) => {
      const digits = currencyDigits(currency);
      const amount = new Intl.NumberFormat(format.locale, {
        style: "currency",
        currency,
        minimumFractionDigits: digits + 1,
        maximumFractionDigits: digits + 1,
      }).format(thousandths / 1000 / 10 ** digits);
      return `${amount}/${t(`vehicleManagement.fuelUnits.${unit}`)}`;
    },
  };
}

/*
 * Deleting waits a few seconds so the toast's Undo can take it back: the row hides at once, and
 * the delete request goes only if nobody undoes it. The pending ids live outside React, so a
 * delete still goes through when the page that started it is left.
 */
const pending = new Set<string>();
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<string> = new Set();
const emit = () => {
  snapshot = new Set(pending);
  for (const listener of listeners) listener();
};

export const UNDO_MS = 6000;

export function hideUntilDeleted(id: string, remove: () => void): () => void {
  pending.add(id);
  emit();
  const timer = setTimeout(() => {
    remove();
    // The row stays hidden; the refetch after the delete drops it for good.
    setTimeout(() => {
      pending.delete(id);
      emit();
    }, 2000);
  }, UNDO_MS);
  return () => {
    clearTimeout(timer);
    pending.delete(id);
    emit();
  };
}

/** The expenses waiting to be deleted, which lists leave out. */
export function useHiddenExpenses(): ReadonlySet<string> {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => snapshot,
  );
}
